import React, { useState, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Upload, Download, Trash2, Menu, LogOut, Image as ImageIcon, ZoomIn, ZoomOut, Crop, X, Check } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import HreflangTags from '../components/HreflangTags';
import Cropper from 'react-easy-crop';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import MobileMenu from '../components/MobileMenu';
import SiteFooter from '../components/SiteFooter';
import NotificationBell from '../components/NotificationBell';
import LanguageSelector from '../components/LanguageSelector';
import ThemeToggle from '../components/ThemeToggle';
import ToolSidebar from '../components/ToolSidebar';
import '../styles/ImageResizePage.css';

const formatBytes = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

// Helper to crop the image using canvas
const getCroppedImg = (imageSrc, pixelCrop) => {
  return new Promise((resolve) => {
    const image = new window.Image();
    image.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = pixelCrop.width;
      canvas.height = pixelCrop.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, pixelCrop.x, pixelCrop.y, pixelCrop.width, pixelCrop.height, 0, 0, pixelCrop.width, pixelCrop.height);
      resolve(canvas.toDataURL('image/png'));
    };
    image.src = imageSrc;
  });
};

const ASPECT_OPTIONS = [
  { label: 'Free', value: null },
  { label: '1:1', value: 1 },
  { label: '4:3', value: 4 / 3 },
  { label: '16:9', value: 16 / 9 },
  { label: '3:2', value: 3 / 2 },
  { label: '2:3', value: 2 / 3 },
];

const ImageResizePage = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [origWidth, setOrigWidth] = useState(0);
  const [origHeight, setOrigHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [keepRatio, setKeepRatio] = useState(true);
  const [quality, setQuality] = useState(80);
  const [format, setFormat] = useState('jpeg');
  const [outputSize, setOutputSize] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Crop state
  const [cropMode, setCropMode] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [cropAspect, setCropAspect] = useState(null);

  const fileInputRef = useRef(null);
  const canvasRef = useRef(null);
  const { t } = useLanguage();
  const { theme } = useTheme();
  const { user, isAuthenticated, logout } = useAuth();

  const LOGO_DARK = "https://customer-assets.emergentagent.com/job_422444ca-8f0f-4ebc-8f78-bee66ce7f723/artifacts/qo6wuzuf_REGISTAN%20LOGO.png";
  const LOGO_LIGHT = "https://customer-assets.emergentagent.com/job_9d222022-9462-4aac-aca8-59da15d4f381/artifacts/tueqptuk_registan%20logo.png";
  const currentLogo = theme === 'light' ? LOGO_LIGHT : LOGO_DARK;

  const aspectRatio = origWidth && origHeight ? origWidth / origHeight : 1;

  const handleFile = useCallback((file) => {
    if (!file || !file.type.startsWith('image/')) return;
    setOriginalFile(file);
    const url = URL.createObjectURL(file);
    setOriginalImage(url);
    setOutputSize(null);
    setPreviewUrl(null);
    setCropMode(false);

    const img = new window.Image();
    img.onload = () => {
      setOrigWidth(img.width);
      setOrigHeight(img.height);
      setWidth(img.width);
      setHeight(img.height);
    };
    img.src = url;
  }, []);

  const onDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleWidthChange = (val) => {
    const w = Math.max(1, parseInt(val) || 0);
    setWidth(w);
    if (keepRatio) setHeight(Math.round(w / aspectRatio));
  };

  const handleHeightChange = (val) => {
    const h = Math.max(1, parseInt(val) || 0);
    setHeight(h);
    if (keepRatio) setWidth(Math.round(h * aspectRatio));
  };

  const applyScale = (scale) => {
    const w = Math.round(origWidth * scale);
    const h = Math.round(origHeight * scale);
    setWidth(w);
    setHeight(h);
  };

  const onCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);

  const applyCrop = async () => {
    if (!croppedAreaPixels || !originalImage) return;
    const croppedUrl = await getCroppedImg(originalImage, croppedAreaPixels);
    // Replace original with cropped version
    setOriginalImage(croppedUrl);
    setPreviewUrl(null);
    setOutputSize(null);
    // Update dimensions
    setOrigWidth(croppedAreaPixels.width);
    setOrigHeight(croppedAreaPixels.height);
    setWidth(croppedAreaPixels.width);
    setHeight(croppedAreaPixels.height);
    setCropMode(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
  };

  const processImage = useCallback(() => {
    if (!originalImage) return;
    const img = new window.Image();
    img.onload = () => {
      const canvas = canvasRef.current;
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(img, 0, 0, width, height);

      let mimeType = 'image/jpeg';
      if (format === 'png') mimeType = 'image/png';
      else if (format === 'webp') mimeType = 'image/webp';

      const q = format === 'png' ? undefined : quality / 100;
      canvas.toBlob((blob) => {
        if (blob) {
          setOutputSize(blob.size);
          setPreviewUrl(URL.createObjectURL(blob));
        }
      }, mimeType, q);
    };
    img.src = originalImage;
  }, [originalImage, width, height, quality, format]);

  const handleDownload = () => {
    if (!previewUrl) return;
    const ext = format === 'jpeg' ? 'jpg' : format;
    const name = originalFile?.name?.replace(/\.[^.]+$/, '') || 'image';
    const a = document.createElement('a');
    a.href = previewUrl;
    a.download = `${name}-${width}x${height}.${ext}`;
    a.click();
  };

  const handleReset = () => {
    setOriginalImage(null);
    setOriginalFile(null);
    setPreviewUrl(null);
    setOutputSize(null);
    setWidth(0);
    setHeight(0);
    setOrigWidth(0);
    setOrigHeight(0);
    setCropMode(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const reduction = originalFile && outputSize
    ? Math.round((1 - outputSize / originalFile.size) * 100)
    : null;

  return (
    <div className="ir-page" data-testid="imageresize-page">
      <Helmet>
        <title>Free Image Resizer &amp; Compressor Online 2026 | Reduce Photo Size Instantly</title>
        <meta name="description" content="Resize and compress images online for free — no signup, no watermark. Reduce image file size by up to 90%, change dimensions, adjust quality from 1-100%, and convert between JPG, PNG, WebP. Perfect for visa photos, passport images, web optimization, email attachments and social media. Works on desktop and mobile." />
        <meta name="keywords" content="image resizer, compress image online free, reduce image size, resize photo online, image compressor, reduce file size, jpg compressor, png resizer, webp converter, free image resize tool, photo resizer, image optimizer, compress jpeg, reduce photo size, image size reducer, compress photo for visa, passport photo resize, resize image for email, bulk image compressor, image quality reducer, photo compressor online, compress png, resize picture, image dimensions changer, photo size reducer free 2026" />
        <link rel="canonical" href="https://registantours.com/imageresize" />
        <meta property="og:title" content="Free Image Resizer & Compressor | Reduce Photo Size Online" />
        <meta property="og:description" content="Resize, compress and convert images online for free. Reduce file size by up to 90%. JPG, PNG, WebP. No signup, instant download." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://registantours.com/imageresize" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Free Image Resizer & Compressor Online 2026" />
        <meta name="twitter:description" content="Reduce image size, change dimensions, adjust quality. Free online tool for JPG, PNG, WebP. No registration required." />
      </Helmet>
      <HreflangTags path="/imageresize" />

      <header className="dark-header" data-testid="ir-header">
        <div className="header-left">
          <div className="header-logo">
            <Link to="/"><img src={currentLogo} alt="Registan" className="logo-img" /></Link>
          </div>
          <LanguageSelector />
          <ThemeToggle />
        </div>
        <nav className="dark-nav desktop-hide-nav">
          <Link to="/" className="dark-nav-link">{t('home') || 'Home'}</Link>
          <Link to="/start-business" className="dark-nav-link">{t('startBusiness') || 'Start Business'}</Link>
          <Link to="/tourismsoftware" className="dark-nav-link">{t('tourismSoftware') || 'Tourism Software'}</Link>
          <Link to="/about" className="dark-nav-link">{t('about') || 'About'}</Link>
        </nav>
        {isAuthenticated ? (
          <div className="header-user-menu">
            <NotificationBell userEmail={user?.email} />
            <button className="btn-user-profile" onClick={() => window.location.href = '/dashboard'}>
              <div className="user-avatar-small">{user?.name?.charAt(0)?.toUpperCase()}</div>
              <span>{user?.name}</span>
            </button>
            <button className="btn-logout-header" onClick={logout} title="Logout"><LogOut size={20} /></button>
          </div>
        ) : (
          <div className="header-auth-buttons">
            <button className="btn-secondary btn-login" onClick={() => window.location.href = '/login'}>{t('login')}</button>
            <button className="btn-primary btn-signup" onClick={() => window.location.href = '/register'}>{t('signUp')}</button>
          </div>
        )}
        <button className="mobile-menu-toggle" onClick={() => setIsMobileMenuOpen(true)} data-testid="ir-mobile-menu-toggle">
          <Menu size={24} />
        </button>
      </header>

      <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />

      <div className="tool-page-layout">
        <ToolSidebar />
        <main className="ir-main tool-page-content">
        <div className="ir-hero">
          <h1 className="ir-title" data-testid="ir-title">{t('imageResizerTitle') || 'Image Resizer & Compressor'}</h1>
          <p className="ir-subtitle">{t('imageResizerSubtitle') || 'Resize, crop, compress and convert your images. Reduce file size without losing quality.'}</p>
        </div>

        {!originalImage ? (
          <div
            className={`ir-dropzone ${dragOver ? 'ir-dropzone-active' : ''}`}
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            onClick={() => fileInputRef.current?.click()}
            data-testid="ir-dropzone"
          >
            <Upload size={36} className="ir-drop-icon" />
            <p className="ir-drop-text">{t('dropImageUpload') || 'Drop your image here or tap to upload'}</p>
            <p className="ir-drop-sub">{t('supportedFormats') || 'PNG, JPG, WEBP up to 50MB'}</p>
            <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={(e) => handleFile(e.target.files?.[0])} data-testid="ir-file-input" />
          </div>
        ) : (
          <div className="ir-workspace" data-testid="ir-workspace">
            {/* Controls Panel */}
            <div className="ir-controls">
              <div className="ir-info-bar">
                <div className="ir-info-item">
                  <span className="ir-info-label">{t('original') || 'Original'}</span>
                  <span className="ir-info-value">{origWidth} x {origHeight}</span>
                </div>
                <div className="ir-info-item">
                  <span className="ir-info-label">{t('fileSize') || 'File Size'}</span>
                  <span className="ir-info-value">{formatBytes(originalFile?.size || 0)}</span>
                </div>
              </div>

              {/* Crop Button */}
              <button
                className={`ir-crop-toggle ${cropMode ? 'ir-crop-toggle-active' : ''}`}
                onClick={() => setCropMode(!cropMode)}
                data-testid="ir-crop-toggle"
              >
                <Crop size={16} />
                {cropMode ? t('cancelCrop') || 'Cancel Crop' : t('cropImage') || 'Crop Image'}
              </button>

              {/* Crop controls (shown when crop mode active) */}
              {cropMode && (
                <div className="ir-crop-controls" data-testid="ir-crop-controls">
                  <div className="ir-section-title">{t('aspectRatio') || 'Aspect Ratio'}</div>
                  <div className="ir-aspect-row">
                    {ASPECT_OPTIONS.map((opt) => (
                      <button
                        key={opt.label}
                        className={`ir-aspect-btn ${cropAspect === opt.value ? 'ir-aspect-active' : ''}`}
                        onClick={() => setCropAspect(opt.value)}
                        data-testid={`ir-aspect-${opt.label}`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                  <div className="ir-crop-zoom-row">
                    <ZoomOut size={14} />
                    <input
                      type="range"
                      min="1"
                      max="3"
                      step="0.1"
                      value={zoom}
                      onChange={(e) => setZoom(parseFloat(e.target.value))}
                      className="ir-slider"
                      data-testid="ir-crop-zoom"
                    />
                    <ZoomIn size={14} />
                  </div>
                  <button className="ir-crop-apply-btn" onClick={applyCrop} data-testid="ir-crop-apply">
                    <Check size={16} /> {t('applyCrop') || 'Apply Crop'}
                  </button>
                </div>
              )}

              {/* Dimensions (hidden during crop) */}
              {!cropMode && (
                <>
                  <div className="ir-section">
                    <div className="ir-section-title">{t('dimensions') || 'Dimensions'}</div>
                    <div className="ir-dim-row">
                      <div className="ir-dim-field">
                        <label>{t('widthPx') || 'Width (px)'}</label>
                        <input type="number" value={width} onChange={(e) => handleWidthChange(e.target.value)} min="1" data-testid="ir-width" />
                      </div>
                      <div className="ir-dim-field">
                        <label>{t('heightPx') || 'Height (px)'}</label>
                        <input type="number" value={height} onChange={(e) => handleHeightChange(e.target.value)} min="1" data-testid="ir-height" />
                      </div>
                    </div>
                    <label className="ir-checkbox">
                      <input type="checkbox" checked={keepRatio} onChange={(e) => setKeepRatio(e.target.checked)} data-testid="ir-keep-ratio" />
                      <span>{t('keepAspectRatio') || 'Keep aspect ratio'}</span>
                    </label>
                    <div className="ir-presets">
                      <button onClick={() => applyScale(1)} className={width === origWidth ? 'active' : ''}>100%</button>
                      <button onClick={() => applyScale(0.75)}>75%</button>
                      <button onClick={() => applyScale(0.5)}>50%</button>
                      <button onClick={() => applyScale(0.25)}>25%</button>
                    </div>
                  </div>

                  <div className="ir-section">
                    <div className="ir-section-title">{t('qualityLabel') || 'Quality'}</div>
                    <div className="ir-quality-row">
                      <input type="range" min="1" max="100" value={quality} onChange={(e) => setQuality(parseInt(e.target.value))} className="ir-slider" data-testid="ir-quality" />
                      <span className="ir-quality-val">{quality}%</span>
                    </div>
                    <div className="ir-quality-labels">
                      <span>{t('lowQuality') || 'Low quality / Small size'}</span>
                      <span>{t('highQuality') || 'High quality / Large size'}</span>
                    </div>
                  </div>

                  <div className="ir-section">
                    <div className="ir-section-title">{t('outputFormat') || 'Output Format'}</div>
                    <div className="ir-format-row">
                      {['jpeg', 'png', 'webp'].map((f) => (
                        <button key={f} className={`ir-format-btn ${format === f ? 'ir-format-active' : ''}`} onClick={() => setFormat(f)} data-testid={`ir-format-${f}`}>
                          {f.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </div>

                  <button className="ir-apply-btn" onClick={processImage} data-testid="ir-apply-btn">
                    <ZoomOut size={18} /> {t('resizeCompress') || 'Resize & Compress'}
                  </button>

                  {outputSize !== null && (
                    <div className="ir-output-stats" data-testid="ir-output-stats">
                      <div className="ir-stat">
                        <span>{t('newSize') || 'New Size'}</span>
                        <strong>{formatBytes(outputSize)}</strong>
                      </div>
                      <div className="ir-stat">
                        <span>{t('dimensions') || 'Dimensions'}</span>
                        <strong>{width} x {height}</strong>
                      </div>
                      {reduction !== null && (
                        <div className={`ir-stat ir-stat-reduction ${reduction > 0 ? 'ir-stat-green' : 'ir-stat-red'}`}>
                          <span>{t('reduction') || 'Reduction'}</span>
                          <strong>{reduction > 0 ? `-${reduction}%` : `+${Math.abs(reduction)}%`}</strong>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="ir-action-row">
                    {previewUrl && (
                      <button className="ir-btn ir-btn-download" onClick={handleDownload} data-testid="ir-download-btn">
                        <Download size={18} /> {t('download') || 'Download'}
                      </button>
                    )}
                    <button className="ir-btn ir-btn-reset" onClick={handleReset} data-testid="ir-reset-btn">
                      <Trash2 size={18} /> {t('remove') || 'Remove'}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Preview Panel */}
            <div className="ir-preview-panel">
              <div className="ir-preview-label">
                {cropMode ? (
                  <><Crop size={14} /> {t('cropModeDrag') || 'Crop Mode — drag to adjust'}</>
                ) : (
                  <><ImageIcon size={14} /> {previewUrl ? (t('processedPreview') || 'Processed Preview') : (t('originalImage') || 'Original Image')}</>
                )}
              </div>
              <div className={`ir-preview-box ${cropMode ? 'ir-preview-crop' : ''}`}>
                {cropMode ? (
                  <div className="ir-cropper-container">
                    <Cropper
                      image={originalImage}
                      crop={crop}
                      zoom={zoom}
                      aspect={cropAspect || undefined}
                      onCropChange={setCrop}
                      onCropComplete={onCropComplete}
                      onZoomChange={setZoom}
                    />
                  </div>
                ) : (
                  <img src={previewUrl || originalImage} alt="Preview" className="ir-preview-img" data-testid="ir-preview-img" />
                )}
              </div>
            </div>
          </div>
        )}

        {/* Features */}
        {!originalImage && (
          <div className="ir-features">
            <div className="ir-feature-card">
              <Crop size={24} />
              <h3>{t('cropFeatureTitle') || 'Crop Image'}</h3>
              <p>{t('cropFeatureDesc') || 'Crop your image with free or fixed aspect ratios like 1:1, 4:3, 16:9.'}</p>
            </div>
            <div className="ir-feature-card">
              <ZoomOut size={24} />
              <h3>{t('reduceFileSizeTitle') || 'Reduce File Size'}</h3>
              <p>{t('reduceFileSizeDesc') || 'Compress images by adjusting quality. Perfect for web, email and social media.'}</p>
            </div>
            <div className="ir-feature-card">
              <ZoomIn size={24} />
              <h3>{t('customDimensionsTitle') || 'Custom Dimensions'}</h3>
              <p>{t('customDimensionsDesc') || 'Set exact width and height in pixels, or use quick presets like 50%, 75%.'}</p>
            </div>
            <div className="ir-feature-card">
              <ImageIcon size={24} />
              <h3>{t('convertFormatTitle') || 'Convert Format'}</h3>
              <p>{t('convertFormatDesc') || 'Convert between JPG, PNG and WebP. Choose the best format for your needs.'}</p>
            </div>
          </div>
        )}

        <canvas ref={canvasRef} style={{ display: 'none' }} />
      </main>
      </div>

      <SiteFooter />
    </div>
  );
};

export default ImageResizePage;
